// Optional financial-tracking + document sections for the simplified global
// ("Somewhere Else" — Beta) property workflow ONLY. These are additive and are
// never rendered for the fully-supported country markets, so the three
// country-specific fee/ROI engines remain untouched.
//
// NO country-specific taxes are ever computed here. Every amount is entered
// manually by the user. Where a figure is in a currency other than the
// property's base currency and no exchange rate is provided, it is shown but
// explicitly EXCLUDED from base-currency totals (an honest incomplete state).
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Plus, Trash2, ChevronDown, FileText, UploadCloud, Loader2,
  AlertTriangle, Wallet, Receipt, Home, Download, Eye,
} from "lucide-react";
import api from "../lib/api";
import { toast } from "../components/ui/sonner";
import { CURRENCIES } from "../lib/worldData";

// ── shared styling (EQUIA dark + gold) ──────────────────────────────────────
const inputCls =
  "w-full bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm placeholder:text-white/35 focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none";
const monoInputCls = `${inputCls} font-mono`;
// Native date inputs need breathing room or the value renders as "mm/dc…".
const dateInputCls =
  "w-full bg-[#050505] border border-white/10 text-white px-2 py-2 text-sm focus:border-[#D4AF37] focus:ring-1 focus:ring-[#D4AF37] transition-all outline-none";
const selectCls =
  "w-full bg-[#050505] border border-white/10 text-white px-3 py-2 text-sm focus:border-[#D4AF37] outline-none";
const miniLabel = "text-[9px] tracking-[0.14em] uppercase text-white/45 mb-1 block";

const newId = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const toNum = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// Convert a line amount into the property's base currency. Returns null when it
// cannot be done honestly (different currency with no exchange rate).
export function toBaseAmount(amount, currency, rate, baseCurrency) {
  const amt = toNum(amount);
  if (amt === null) return null;
  if (!currency || currency === baseCurrency) return amt;
  const r = toNum(rate);
  if (r !== null && r > 0) return amt * r;
  return null;
}

const CURRENCY_OPTIONS = CURRENCIES;

const fmtMoney = (n, cur) =>
  `${cur || ""} ${Math.round(Number(n) || 0).toLocaleString("en-US")}`.trim();

// ── collapsible panel ───────────────────────────────────────────────────────
function Panel({ icon: Icon, title, subtitle, badge, open, onToggle, testId, children }) {
  return (
    <div className="border border-white/10 bg-white/[0.015]">
      <button
        type="button"
        onClick={onToggle}
        data-testid={testId}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
      >
        <span className="w-8 h-8 shrink-0 flex items-center justify-center border border-white/10 text-[#D4AF37]">
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center gap-2">
            <span className="text-white text-sm font-sans">{title}</span>
            {badge ? (
              <span className="text-[9px] tracking-[0.1em] uppercase text-[#D4AF37] border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-1.5 py-0.5">
                {badge}
              </span>
            ) : null}
          </span>
          {subtitle ? (
            <span className="block text-[11px] text-white/40 mt-0.5 truncate">{subtitle}</span>
          ) : null}
        </span>
        <ChevronDown
          className={`w-4 h-4 text-white/45 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="px-4 pb-4 pt-1 border-t border-white/[0.06]">{children}</div>}
    </div>
  );
}

const RowShell = ({ children, testId }) => (
  <div
    data-testid={testId}
    className="grid gap-2 items-end border border-white/[0.07] bg-[#050505]/60 p-3 mb-2"
  >
    {children}
  </div>
);

const RemoveBtn = ({ onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="inline-flex items-center justify-center h-9 w-9 border border-white/10 text-white/50 hover:text-red-400 hover:border-red-400/40 transition-colors"
    title="Remove"
  >
    <Trash2 className="w-3.5 h-3.5" />
  </button>
);

const AddBtn = ({ onClick, testId, children }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="inline-flex items-center gap-2 px-3 py-2 border border-[#D4AF37]/40 text-[#D4AF37] text-[11px] tracking-[0.12em] uppercase hover:bg-[#D4AF37]/10 transition-colors"
  >
    <Plus className="w-3.5 h-3.5" /> {children}
  </button>
);

const CurrencySelect = ({ value, onChange, testId }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className={selectCls} data-testid={testId}>
    {CURRENCY_OPTIONS.map((c) => (
      <option key={c.code} value={c.code}>{c.code}</option>
    ))}
  </select>
);

const IncompleteNote = ({ children }) => (
  <p className="flex items-start gap-1.5 text-[11px] text-amber-400/90 mt-2">
    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
    <span>{children}</span>
  </p>
);

// ════════════════════════════════════════════════════════════════════════════
// A. PAYMENT TRACKING
// ════════════════════════════════════════════════════════════════════════════
const PAYMENT_STATUSES = [
  { v: "pending", label: "Pending" },
  { v: "paid", label: "Paid" },
  { v: "overdue", label: "Overdue" },
];

export function PaymentTrackingSection({ open, onToggle, payments, setPayments, baseCurrency, purchasePrice }) {
  const add = () =>
    setPayments((p) => [
      ...p,
      { id: newId(), name: "", amount: "", currency: baseCurrency || "", due_date: "", status: "pending", paid_date: "", exchange_rate: "" },
    ]);
  const update = (id, patch) =>
    setPayments((p) => p.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const remove = (id) => setPayments((p) => p.filter((x) => x.id !== id));

  let paidBase = 0, remainingBase = 0, incomplete = false;
  payments.forEach((p) => {
    if (p.amount === "" || p.amount == null) return;
    const base = toBaseAmount(p.amount, p.currency, p.exchange_rate, baseCurrency);
    if (base === null) { incomplete = true; return; }
    if (p.status === "paid") paidBase += base;
    else remainingBase += base;
  });

  const badge = payments.length ? `${payments.length}` : null;
  return (
    <Panel
      icon={Wallet}
      title="Payment Tracking"
      subtitle="Installments, paid amounts, dates, currencies & status"
      badge={badge}
      open={open}
      onToggle={onToggle}
      testId="global-section-toggle-payments"
    >
      <p className="text-[11px] text-white/45 leading-relaxed mb-3">
        Add each purchase payment as its own line. "Paid so far" and "Remaining"
        are calculated from the lines below, so a payment is never counted twice.
      </p>

      {payments.map((p, i) => {
        const foreign = p.currency && p.currency !== baseCurrency;
        return (
          <RowShell key={p.id} testId={`global-payment-row-${i}`}>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
              <div className="sm:col-span-3">
                <label className={miniLabel}>Label</label>
                <input
                  className={inputCls}
                  placeholder={`Installment ${i + 1}`}
                  value={p.name}
                  onChange={(e) => update(p.id, { name: e.target.value })}
                  data-testid={`global-payment-name-${i}`}
                />
              </div>
              <div className="sm:col-span-3">
                <label className={miniLabel}>Amount</label>
                <input
                  type="number" min="0" step="0.01"
                  className={monoInputCls}
                  placeholder="0"
                  value={p.amount}
                  onChange={(e) => update(p.id, { amount: e.target.value })}
                  data-testid={`global-payment-amount-${i}`}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={miniLabel}>Currency</label>
                <CurrencySelect
                  value={p.currency}
                  onChange={(v) => update(p.id, { currency: v })}
                  testId={`global-payment-currency-${i}`}
                />
              </div>
              <div className="sm:col-span-3">
                <label className={miniLabel}>Due date</label>
                <input
                  type="date"
                  className={dateInputCls}
                  value={p.due_date}
                  onChange={(e) => update(p.id, { due_date: e.target.value })}
                  data-testid={`global-payment-due-${i}`}
                />
              </div>
              <div className="sm:col-span-1 flex justify-end">
                <RemoveBtn onClick={() => remove(p.id)} testId={`global-payment-remove-${i}`} />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end mt-2">
              <div className="sm:col-span-3">
                <label className={miniLabel}>Status</label>
                <select
                  className={selectCls}
                  value={p.status}
                  onChange={(e) => update(p.id, { status: e.target.value })}
                  data-testid={`global-payment-status-${i}`}
                >
                  {PAYMENT_STATUSES.map((s) => (
                    <option key={s.v} value={s.v}>{s.label}</option>
                  ))}
                </select>
              </div>
              {p.status === "paid" && (
                <div className="sm:col-span-3">
                  <label className={miniLabel}>Actual payment date</label>
                  <input
                    type="date"
                    className={dateInputCls}
                    value={p.paid_date}
                    onChange={(e) => update(p.id, { paid_date: e.target.value })}
                    data-testid={`global-payment-paiddate-${i}`}
                  />
                </div>
              )}
              {foreign && (
                <div className="sm:col-span-3">
                  <label className={miniLabel}>Rate → {baseCurrency}</label>
                  <input
                    type="number" min="0" step="0.000001"
                    className={monoInputCls}
                    placeholder="actual rate"
                    value={p.exchange_rate}
                    onChange={(e) => update(p.id, { exchange_rate: e.target.value })}
                    data-testid={`global-payment-rate-${i}`}
                  />
                </div>
              )}
            </div>
            {foreign && toBaseAmount(p.amount, p.currency, p.exchange_rate, baseCurrency) === null && p.amount !== "" && (
              <IncompleteNote>
                In {p.currency}, not {baseCurrency}. Add an actual exchange rate to
                include this payment in your capital totals.
              </IncompleteNote>
            )}
          </RowShell>
        );
      })}

      <AddBtn onClick={add} testId="global-add-payment-btn">Add Payment</AddBtn>

      {payments.length > 0 && (
        <div
          className="mt-3 grid grid-cols-2 gap-3 border-t border-white/[0.08] pt-3"
          data-testid="global-payment-summary"
        >
          <div>
            <p className={miniLabel}>Paid so far</p>
            <p className="font-mono text-sm text-[#D4AF37]">{fmtMoney(paidBase, baseCurrency)}</p>
          </div>
          <div>
            <p className={miniLabel}>Remaining installments</p>
            <p className="font-mono text-sm text-white/90">{fmtMoney(remainingBase, baseCurrency)}</p>
          </div>
          {incomplete && (
            <div className="col-span-2">
              <IncompleteNote>
                One or more payments are in another currency without a rate — totals
                above exclude them and your portfolio capital will show as incomplete.
              </IncompleteNote>
            </div>
          )}
          {!incomplete && (() => {
            const price = toNum(purchasePrice);
            if (price === null || price <= 0) return null;
            const unscheduled = Math.round((price - paidBase - remainingBase) * 100) / 100;
            if (Math.abs(unscheduled) < 1) return null;
            return (
              <div className="col-span-2">
                <IncompleteNote>
                  {unscheduled > 0
                    ? `Payments entered (${fmtMoney(paidBase + remainingBase, baseCurrency)}) are ${fmtMoney(unscheduled, baseCurrency)} short of the purchase price (${fmtMoney(price, baseCurrency)}). Add the remaining installments for an accurate Outstanding figure.`
                    : `Payments entered exceed the purchase price by ${fmtMoney(-unscheduled, baseCurrency)} — double-check your installments.`}
                </IncompleteNote>
              </div>
            );
          })()}
        </div>
      )}
    </Panel>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// B. CUSTOM COSTS
// ════════════════════════════════════════════════════════════════════════════
const COST_TYPES = [
  { v: "tax", label: "Tax Payment" },
  { v: "legal", label: "Legal Fees" },
  { v: "notary", label: "Notary Fees" },
  { v: "registration", label: "Registration Fees" },
  { v: "agent_commission", label: "Agent Commission" },
  { v: "other", label: "Other Cost" },
];

export function CustomCostsSection({ open, onToggle, costs, setCosts, baseCurrency }) {
  const add = () =>
    setCosts((c) => [
      ...c,
      { id: newId(), type: "tax", label: "", amount: "", currency: baseCurrency || "", date: "", exchange_rate: "" },
    ]);
  const update = (id, patch) =>
    setCosts((c) => c.map((x) => (x.id === id ? { ...x, ...patch } : x)));
  const remove = (id) => setCosts((c) => c.filter((x) => x.id !== id));

  let totalBase = 0, excluded = false;
  costs.forEach((c) => {
    if (c.amount === "" || c.amount == null) return;
    const base = toBaseAmount(c.amount, c.currency, c.exchange_rate, baseCurrency);
    if (base === null) { excluded = true; return; }
    totalBase += base;
  });

  const badge = costs.length ? `${costs.length}` : null;
  return (
    <Panel
      icon={Receipt}
      title="Custom Costs"
      subtitle="Tax, legal, notary, registration, agent & other — entered manually"
      badge={badge}
      open={open}
      onToggle={onToggle}
      testId="global-section-toggle-costs"
    >
      <p className="text-[11px] text-white/45 leading-relaxed mb-3">
        Enter acquisition costs yourself — nothing is calculated automatically for
        this country. Costs in {baseCurrency || "the property currency"} are added
        to Total Investment.
      </p>

      {costs.map((c, i) => {
        const foreign = c.currency && c.currency !== baseCurrency;
        return (
          <RowShell key={c.id} testId={`global-cost-row-${i}`}>
            <div className="grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
              <div className="sm:col-span-4">
                <label className={miniLabel}>Type</label>
                <select
                  className={selectCls}
                  value={c.type}
                  onChange={(e) => update(c.id, { type: e.target.value })}
                  data-testid={`global-cost-type-${i}`}
                >
                  {COST_TYPES.map((t) => (
                    <option key={t.v} value={t.v}>{t.label}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className={miniLabel}>Amount</label>
                <input
                  type="number" min="0" step="0.01"
                  className={monoInputCls}
                  placeholder="0"
                  value={c.amount}
                  onChange={(e) => update(c.id, { amount: e.target.value })}
                  data-testid={`global-cost-amount-${i}`}
                />
              </div>
              <div className="sm:col-span-2">
                <label className={miniLabel}>Currency</label>
                <CurrencySelect
                  value={c.currency}
                  onChange={(v) => update(c.id, { currency: v })}
                  testId={`global-cost-currency-${i}`}
                />
              </div>
              <div className="sm:col-span-3">
                <label className={miniLabel}>Date</label>
                <input
                  type="date"
                  className={dateInputCls}
                  value={c.date}
                  onChange={(e) => update(c.id, { date: e.target.value })}
                  data-testid={`global-cost-date-${i}`}
                />
              </div>
              <div className="sm:col-span-1 flex justify-end">
                <RemoveBtn onClick={() => remove(c.id)} testId={`global-cost-remove-${i}`} />
              </div>
            </div>
            {foreign && (
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-12 gap-2 items-end">
                <div className="sm:col-span-4">
                  <label className={miniLabel}>Rate → {baseCurrency}</label>
                  <input
                    type="number" min="0" step="0.000001"
                    className={monoInputCls}
                    placeholder="actual rate"
                    value={c.exchange_rate}
                    onChange={(e) => update(c.id, { exchange_rate: e.target.value })}
                    data-testid={`global-cost-rate-${i}`}
                  />
                </div>
              </div>
            )}
            {c.type === "other" && (
              <div className="mt-2">
                <label className={miniLabel}>Description</label>
                <input
                  className={inputCls}
                  placeholder="What is this cost?"
                  value={c.label}
                  onChange={(e) => update(c.id, { label: e.target.value })}
                  data-testid={`global-cost-label-${i}`}
                />
              </div>
            )}
            {foreign && toBaseAmount(c.amount, c.currency, c.exchange_rate, baseCurrency) === null && c.amount !== "" && (
              <IncompleteNote>
                In {c.currency}, not {baseCurrency}. Add an actual exchange rate to
                include this cost in Total Investment.
              </IncompleteNote>
            )}
          </RowShell>
        );
      })}

      <AddBtn onClick={add} testId="global-add-cost-btn">Add Custom Cost</AddBtn>

      {costs.length > 0 && (
        <div className="mt-3 border-t border-white/[0.08] pt-3" data-testid="global-costs-total">
          <p className={miniLabel}>Total custom costs (base currency)</p>
          <p className="font-mono text-sm text-[#D4AF37]">{fmtMoney(totalBase, baseCurrency)}</p>
          {excluded && (
            <IncompleteNote>
              Some costs are in another currency and are excluded from this total.
            </IncompleteNote>
          )}
        </div>
      )}
    </Panel>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// C. RENTAL INCOME & EXPENSES (actual vs projected)
// ════════════════════════════════════════════════════════════════════════════
const RENTAL_FIELDS = [
  { k: "income_monthly", label: "Monthly rental income" },
  { k: "maintenance_annual", label: "Maintenance (annual)" },
  { k: "management_annual", label: "Management fees (annual)" },
  { k: "other_annual", label: "Other recurring (annual)" },
];

function RentalColumn({ title, variant, data, onChange, baseCurrency }) {
  return (
    <div className="border border-white/[0.07] bg-[#050505]/60 p-3">
      <p className="text-[10px] tracking-[0.14em] uppercase mb-3 flex items-center gap-2">
        <span className={variant === "actual" ? "text-[#D4AF37]" : "text-white/55"}>{title}</span>
        <span className="text-white/30 normal-case tracking-normal text-[10px]">
          ({baseCurrency || "—"})
        </span>
      </p>
      <div className="space-y-2.5">
        {RENTAL_FIELDS.map((f) => (
          <div key={f.k}>
            <label className={miniLabel}>{f.label}</label>
            <input
              type="number" min="0" step="0.01"
              className={monoInputCls}
              placeholder="0"
              value={data[f.k] ?? ""}
              onChange={(e) => onChange({ ...data, [f.k]: e.target.value })}
              data-testid={`global-rental-${variant}-${f.k}`}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

export function RentalTrackingSection({ open, onToggle, rental, setRental, baseCurrency }) {
  const actual = rental.actual || {};
  const projected = rental.projected || {};
  const hasAny =
    RENTAL_FIELDS.some((f) => actual[f.k]) || RENTAL_FIELDS.some((f) => projected[f.k]);
  return (
    <Panel
      icon={Home}
      title="Rental Income & Expenses"
      subtitle="Actual figures drive ROI · projected figures are planning-only"
      badge={hasAny ? "set" : null}
      open={open}
      onToggle={onToggle}
      testId="global-section-toggle-rental"
    >
      <p className="text-[11px] text-white/45 leading-relaxed mb-3">
        Actual income and expenses feed your portfolio ROI. Projected figures are
        kept separately for planning and are never mixed into live totals.
      </p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <RentalColumn
          title="Actual"
          variant="actual"
          data={actual}
          baseCurrency={baseCurrency}
          onChange={(next) => setRental((r) => ({ ...r, actual: next }))}
        />
        <RentalColumn
          title="Projected"
          variant="projected"
          data={projected}
          baseCurrency={baseCurrency}
          onChange={(next) => setRental((r) => ({ ...r, projected: next }))}
        />
      </div>
    </Panel>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// D. DOCUMENTS (edit mode only — reuses the existing Document Vault endpoints,
//    so all current access permissions & security rules are preserved)
// ════════════════════════════════════════════════════════════════════════════
export function GlobalDocumentsSection({ open, onToggle, assetId }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    if (!assetId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/assets/${assetId}/documents`);
      setDocs(Array.isArray(data) ? data : []);
    } catch {
      /* non-fatal */
    } finally {
      setLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    if (open) fetchDocs();
  }, [open, fetchDocs]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !assetId) return;
    e.target.value = "";
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const qs = new URLSearchParams({ related_asset_id: assetId });
      await api.post(`/documents/upload?${qs.toString()}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Document added to the vault");
      fetchDocs();
    } catch (err) {
      toast.error("Upload failed", { description: err.response?.data?.detail });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      await api.delete(`/documents/${docId}`);
      setDocs((d) => d.filter((x) => x.id !== docId));
    } catch (err) {
      toast.error("Could not remove document", { description: err.response?.data?.detail });
    }
  };

  const preview = async (d) => {
    try {
      const resp = await api.get(`/documents/${d.id}/preview`, { responseType: "blob" });
      window.open(URL.createObjectURL(resp.data), "_blank");
    } catch (err) {
      toast.error("Preview unavailable");
    }
  };

  return (
    <Panel
      icon={FileText}
      title="Documents"
      subtitle="Contracts, deeds & receipts — stored in your secure Document Vault"
      badge={docs.length ? `${docs.length}` : null}
      open={open}
      onToggle={onToggle}
      testId="global-section-toggle-documents"
    >
      <div data-testid="global-docs-section">
        {loading ? (
          <p className="text-[12px] text-white/40 py-2 flex items-center gap-2">
            <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading documents…
          </p>
        ) : docs.length === 0 ? (
          <p className="text-[12px] text-white/40 py-2">No documents linked yet.</p>
        ) : (
          <div className="space-y-2 mb-3">
            {docs.map((d) => (
              <div
                key={d.id}
                data-testid={`global-doc-item-${d.id}`}
                className="flex items-center gap-3 border border-white/[0.08] bg-[#050505]/60 px-3 py-2"
              >
                <FileText className="w-4 h-4 text-white/45 shrink-0" />
                <p className="text-white/85 text-[12.5px] truncate flex-1">
                  {d.original_filename || "Document"}
                </p>
                <button
                  type="button"
                  onClick={() => preview(d)}
                  data-testid={`global-doc-preview-${d.id}`}
                  className="text-white/50 hover:text-[#D4AF37] transition-colors"
                  title="Preview"
                >
                  <Eye className="w-4 h-4" />
                </button>
                <button
                  type="button"
                  onClick={() => handleDelete(d.id)}
                  data-testid={`global-doc-remove-${d.id}`}
                  className="text-white/50 hover:text-red-400 transition-colors"
                  title="Remove"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            ))}
          </div>
        )}

        <input ref={inputRef} type="file" className="hidden" onChange={handleFile} data-testid="global-doc-file-input" />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="global-doc-upload-btn"
          className="inline-flex items-center gap-2 px-3 py-2 border border-[#D4AF37]/40 text-[#D4AF37] text-[11px] tracking-[0.12em] uppercase hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-50"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />}
          {uploading ? "Uploading…" : "Upload Document"}
        </button>
      </div>
    </Panel>
  );
}

// ── serialisation helpers used by SimpleGlobalForm.handleSave ────────────────
export function serializePayments(payments, baseCurrency) {
  return payments
    .filter((p) => p.amount !== "" && p.amount != null)
    .map((p) => {
      const base = toBaseAmount(p.amount, p.currency, p.exchange_rate, baseCurrency);
      return {
        id: p.id,
        name: p.name || "Payment",
        due_date: p.due_date || null,
        date: p.due_date || null,
        amount: base,
        original_amount: toNum(p.amount),
        currency: p.currency || baseCurrency,
        exchange_rate: p.exchange_rate !== "" ? toNum(p.exchange_rate) : null,
        status: p.status || "pending",
        is_paid: p.status === "paid",
        paid_date: p.status === "paid" ? (p.paid_date || null) : null,
      };
    });
}

// Sum of custom costs that are in the base currency (honest — foreign-currency
// costs without conversion are excluded). Feeds `other_purchase_costs`.
export function customCostsBaseTotal(costs, baseCurrency) {
  let total = 0;
  costs.forEach((c) => {
    const base = toBaseAmount(c.amount, c.currency, c.exchange_rate, baseCurrency);
    if (base !== null) total += base;
  });
  return Math.round(total * 100) / 100;
}

export function serializeCustomCosts(costs, baseCurrency) {
  return costs
    .filter((c) => c.amount !== "" && c.amount != null)
    .map((c) => ({
      id: c.id,
      type: c.type || "other",
      label: c.label || "",
      amount: toNum(c.amount),
      currency: c.currency || baseCurrency,
      exchange_rate: c.exchange_rate !== "" && c.exchange_rate != null ? toNum(c.exchange_rate) : null,
      date: c.date || null,
    }));
}

// Actual rental figures that drive ROI. Projected values are stored raw but
// never fed into the engine.
export function rentalEngineInputs(rental) {
  const a = rental.actual || {};
  const income = toNum(a.income_monthly) || 0;
  const expenses =
    (toNum(a.maintenance_annual) || 0) +
    (toNum(a.management_annual) || 0) +
    (toNum(a.other_annual) || 0);
  return { monthly_rental_income: income, other_annual_costs: Math.round(expenses * 100) / 100 };
}

export function normalizeRental(rental) {
  const clean = (o = {}) => {
    const out = {};
    RENTAL_FIELDS.forEach((f) => {
      const n = toNum(o[f.k]);
      if (n !== null) out[f.k] = n;
    });
    return out;
  };
  return { actual: clean(rental.actual), projected: clean(rental.projected) };
}
